import { useEffect, type RefObject } from "react";
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Утилита для работы с unified роутами (для iframe встраивания)
 * Unified роуты используют префикс /u/ и отображают контент без header, footer и sidebar
 */

/**
 * Преобразует обычный URL в unified URL
 * @param path - Обычный путь (например, "/projects/123/")
 * @returns Unified путь (например, "/u/projects/123/")
 */
export const toUnifiedRoute = (path: string): string => {
  // Если путь уже unified, возвращаем как есть
  if (path.startsWith("/u/")) {
    return path;
  }

  // Удаляем ведущий слэш если есть
  const normalizedPath = path.startsWith("/") ? path.slice(1) : path;

  return `/u/${normalizedPath}`;
};

/**
 * Преобразует unified URL в обычный URL
 * @param path - Unified путь (например, "/u/projects/123/")
 * @returns Обычный путь (например, "/projects/123/")
 */
export const fromUnifiedRoute = (path: string): string => {
  // Если путь не unified, возвращаем как есть
  if (!path.startsWith("/u/")) {
    return path;
  }

  // Удаляем префикс /u/
  return `/${  path.slice(3)}`;
};

/**
 * Проверяет, является ли текущий путь unified роутом
 * @param path - Путь для проверки
 * @returns true если путь является unified роутом
 */
export const isUnifiedRoute = (path: string): boolean => {
  return path.startsWith("/u/");
};

/**
 * Хук для определения находимся ли мы в unified режиме
 * @returns true если текущий URL является unified роутом
 */
export const useIsUnifiedMode = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  return isUnifiedRoute(window.location.pathname);
};

/**
 * Явно триггерит обновление высоты iframe в unified режиме.
 * Используется после программных изменений DOM (добавление/удаление элементов),
 * чтобы гарантировать обновление высоты iframe.
 * 
 * Вычисляет текущую высоту контента и отправляет сообщение родителю,
 * аналогично тому, как это делает UnifiedLayout.
 * 
 * Вызывается несколько раз с разными задержками, чтобы гарантировать
 * обновление даже при медленном рендеринге React.
 */
export const triggerUnifiedResize = () => {
  if (typeof window === "undefined" || window.parent === window) {
    return; // Не в iframe или не в браузере
  }

  const sendHeight = () => {
    try {
      // Ищем контейнер unified layout (аналогично UnifiedLayout)
      const container = document.querySelector('[data-unified-mode="true"]') as HTMLElement;
      const main = container?.querySelector('main') as HTMLElement;
      
      if (!container || !main) {
        // Fallback: используем высоту документа
        const documentHeight = Math.max(
          document.documentElement.scrollHeight,
          document.documentElement.offsetHeight,
          document.body.scrollHeight,
          document.body.offsetHeight,
        );
        
        window.parent.postMessage(
          {
            type: 'resize',
            height: documentHeight,
          },
          '*',
        );
        return;
      }

      // Важно: при transition `max-height` + `overflow: hidden` у схлопнутых блоков
      // `scrollHeight` НЕ уменьшается. Поэтому здесь приоритет — “видимая” высота.
      const mainScrollHeight = main.scrollHeight;
      const mainOffsetHeight = main.offsetHeight;
      const mainBoundingHeight = main.getBoundingClientRect().height;
      const mainVisibleHeight = Math.max(mainOffsetHeight, mainBoundingHeight);

      const containerScrollHeight = container.scrollHeight;
      const containerOffsetHeight = container.offsetHeight;
      const containerBoundingHeight = container.getBoundingClientRect().height;
      const containerVisibleHeight = Math.max(containerOffsetHeight, containerBoundingHeight);

      const documentHeight = Math.max(
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight,
        document.body.scrollHeight,
        document.body.offsetHeight,
      );

      const containerStyles = window.getComputedStyle(container);
      const paddingTop = parseFloat(containerStyles.paddingTop) || 0;
      const paddingBottom = parseFloat(containerStyles.paddingBottom) || 0;

      const visibleHeight = Math.max(
        mainVisibleHeight + paddingTop + paddingBottom,
        containerVisibleHeight,
      );

      const contentHeight = Math.max(
        mainScrollHeight + paddingTop + paddingBottom,
        containerScrollHeight,
      );

      // Триггер используется чаще для раскрытия/закрытия секций,
      // поэтому приоритет — shrink по видимой высоте.
      // Для роста оставляем подстраховку на content/document.
      const containerHeight = Math.max(visibleHeight, Math.min(contentHeight, documentHeight));

      window.parent.postMessage(
        {
          type: 'resize',
          height: containerHeight,
        },
        '*',
      );
    } catch (error) {
      // Silent error
    }
  };

  // Вызываем несколько раз с разными задержками для гарантии обновления
  // даже при медленном рендеринге React
  // Увеличиваем задержки чтобы DOM успел обновиться после transition
  requestAnimationFrame(() => {
    setTimeout(() => {
      requestAnimationFrame(() => {
        sendHeight(); // Первый вызов через 50ms после RAF (даём время DOM обновиться)
      });
    }, 50);
    
    setTimeout(() => {
      requestAnimationFrame(() => {
        sendHeight(); // Второй вызов через 200ms
      });
    }, 200);
    
    setTimeout(() => {
      requestAnimationFrame(() => {
        sendHeight(); // Третий вызов через 500ms для надежности
      });
    }, 500);
  });
};

/**
 * Хук для автоматического вызова resize при изменении любого поля формы react-hook-form.
 * Подписывается на все изменения формы через watch и триггерит resize в unified режиме.
 * 
 * @param watch - функция watch из react-hook-form (useForm().watch)
 * 
 * @example
 * ```typescript
 * import { useForm } from "react-hook-form";
 * import { useUnifiedFormResize } from "@/shared/utils";
 * 
 * function MyForm() {
 *   const { control, watch, handleSubmit } = useForm();
 *   
 *   // Автоматический resize при любом изменении формы
 *   useUnifiedFormResize(watch);
 *   
 *   return <form>...</form>;
 * }
 * ```
 */
export const useUnifiedFormResize = (watch: any) => {
  const isUnifiedMode = useIsUnifiedMode();

  useEffect(() => {
    if (!isUnifiedMode || !watch) return;

    // Подписываемся на все изменения формы
    const subscription = watch(() => {
      triggerUnifiedResize();
    });

    // Отписываемся при unmount
    return () => subscription.unsubscribe();
  }, [isUnifiedMode, watch]);
};

/**
 * Хук для автоматического вызова resize при любом изменении DOM внутри компонента.
 * Использует MutationObserver для отслеживания изменений.
 * Подходит для страниц без react-hook-form (настройки, списки и т.д.)
 * 
 * @param ref - React ref на контейнер, за которым нужно следить (опционально, по умолчанию document.body)
 * 
 * @example
 * ```typescript
 * import { useUnifiedAutoResize } from "@/shared/utils";
 * 
 * function SettingsPage() {
 *   // Автоматический resize при любом изменении DOM
 *   useUnifiedAutoResize();
 *   
 *   return <div>...</div>;
 * }
 * ```
 */
export const useUnifiedAutoResize = (ref?: RefObject<HTMLElement>) => {
  const isUnifiedMode = useIsUnifiedMode();

  useEffect(() => {
    if (!isUnifiedMode) return;

    // Триггерим resize при монтировании
    triggerUnifiedResize();

    const targetNode = ref?.current || document.body;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const observer = new MutationObserver(() => {
      // Debounce для избежания слишком частых вызовов
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(() => {
        triggerUnifiedResize();
      }, 100);
    });

    observer.observe(targetNode, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    return () => {
      observer.disconnect();
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [isUnifiedMode, ref]);
};
